import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  People as PeopleIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Balance as BalanceIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, GenderEquityData } from '../../services/learningAnalyticsApi';

const GPI_COLORS = {
  near_parity: '#388e3c',
  male_dominant: '#1976d2',
  female_dominant: '#e91e63',
};

const GenderEquityTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GenderEquityData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getGenderEquity(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load gender equity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district, level]);

  const getGPIColor = (gpi: number) => {
    if (gpi >= 0.97 && gpi <= 1.03) return GPI_COLORS.near_parity;
    if (gpi < 0.97) return GPI_COLORS.male_dominant;
    return GPI_COLORS.female_dominant;
  };

  const getGPIChipColor = (status: string): 'success' | 'info' | 'error' => {
    if (status === 'Near Parity') return 'success';
    if (status === 'Male Dominant') return 'info';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, gpi_trends, grade_gender_gap, district_ranking } = data;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {district_ranking.map(d => (
                <MenuItem key={d.district_name} value={d.district_name}>{d.district_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BalanceIcon sx={{ color: getGPIColor(summary.overall_gpi) }} />
                <Typography variant="body2" color="textSecondary">Overall GPI</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold"
                sx={{ color: getGPIColor(summary.overall_gpi) }}>
                {summary.overall_gpi}
              </Typography>
              <Chip label={summary.gpi_status} size="small"
                color={getGPIChipColor(summary.gpi_status)} sx={{ mt: 0.5 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FemaleIcon sx={{ color: '#e91e63' }} />
                <Typography variant="body2" color="textSecondary">Total Female</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.total_female.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <MaleIcon sx={{ color: '#1976d2' }} />
                <Typography variant="body2" color="textSecondary">Total Male</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.total_male.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="action" />
                <Typography variant="body2" color="textSecondary">Gender Gap</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold"
                color={summary.gender_gap >= 0 ? '#e91e63' : '#1976d2'}>
                {summary.gender_gap >= 0 ? '+' : ''}{summary.gender_gap.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {summary.gap_percentage >= 0 ? '+' : ''}{summary.gap_percentage}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* GPI Trends Line Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Gender Parity Index Trends</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                GPI = Female / Male enrollment. Values near 1.0 indicate parity.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={gpi_trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis domain={[0.7, 1.3]} />
                  <Tooltip formatter={(v: number) => v.toFixed(3)} />
                  <Legend />
                  <ReferenceLine y={1.0} stroke="#666" strokeDasharray="5 5" label="Parity" />
                  <Line type="monotone" dataKey="gpi_overall" stroke="#333" strokeWidth={3}
                    name="Overall" dot={{ r: 5 }} />
                  {gpi_trends[0]?.gpi_pre_primary !== undefined && (
                    <Line type="monotone" dataKey="gpi_pre_primary" stroke="#9c27b0"
                      strokeWidth={2} name="Pre-Primary" dot={{ r: 3 }} />
                  )}
                  {gpi_trends[0]?.gpi_primary !== undefined && (
                    <Line type="monotone" dataKey="gpi_primary" stroke="#1976d2"
                      strokeWidth={2} name="Primary" dot={{ r: 3 }} />
                  )}
                  {gpi_trends[0]?.gpi_secondary !== undefined && (
                    <Line type="monotone" dataKey="gpi_secondary" stroke="#f57c00"
                      strokeWidth={2} name="Secondary" dot={{ r: 3 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Grade-by-Grade Gender Bar Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Gender by Grade</Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={grade_gender_gap}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade_label" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip
                    formatter={(v: number, name: string) => [
                      v.toLocaleString(),
                      name === 'female' ? 'Female' : 'Male'
                    ]}
                  />
                  <Legend formatter={(v: string) => v === 'female' ? 'Female' : 'Male'} />
                  <Bar dataKey="male" fill="#1976d2" name="male" />
                  <Bar dataKey="female" fill="#e91e63" name="female" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* District GPI Ranking Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>District Gender Parity Ranking</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Ranked by proximity to gender parity (GPI = 1.0). Closer to 1.0 = better equity.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Female</TableCell>
                  <TableCell align="right">Male</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">GPI</TableCell>
                  <TableCell align="right">Gap</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {district_ranking.map((d) => (
                  <TableRow key={d.district_name} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{d.rank}</Typography>
                    </TableCell>
                    <TableCell>{d.district_name}</TableCell>
                    <TableCell align="right" sx={{ color: '#e91e63' }}>
                      {d.female.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#1976d2' }}>
                      {d.male.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">{d.total.toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" sx={{ color: getGPIColor(d.gpi) }}>
                        {d.gpi.toFixed(3)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right"
                      sx={{ color: d.gap >= 0 ? '#e91e63' : '#1976d2' }}>
                      {d.gap >= 0 ? '+' : ''}{d.gap.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={d.status} size="small"
                        color={getGPIChipColor(d.status)} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GenderEquityTab;
